import { cn } from "@/lib/utils";
import { type DealCategory, getDealCategoryLabel, getDealCategoryColor } from "@/lib/types/deals";
import { Gift, Percent, Star, Ticket, Check, HelpCircle } from "lucide-react";

interface DealTypeFilterProps {
  selectedTypes: DealCategory[] | "All";
  onSelectionChange: (types: DealCategory[] | "All") => void;
  className?: string;
  disableUnclassified?: boolean;
}

const DEAL_CATEGORIES: DealCategory[] = ["BONUS", "DISCOUNT", "POINTS", "IN_KIND", "UNCLASSIFIED"];

const CATEGORY_ICONS: Record<DealCategory, React.ElementType> = {
  BONUS: Gift,
  DISCOUNT: Percent,
  POINTS: Star,
  IN_KIND: Ticket,
  UNCLASSIFIED: HelpCircle,
};

export function DealTypeFilter({ 
  selectedTypes, 
  onSelectionChange, 
  className,
  disableUnclassified = false,
}: DealTypeFilterProps) {
  const isAllSelected = selectedTypes === "All";
  
  const handleAllClick = () => {
    onSelectionChange("All");
  };
  
  const handleTypeClick = (category: DealCategory) => {
    if (isAllSelected) {
      // Switching from All to single selection
      onSelectionChange([category]);
    } else {
      const currentSelection = selectedTypes as DealCategory[];
      const isSelected = currentSelection.includes(category);
      
      if (isSelected) {
        // Deselect this type
        const newSelection = currentSelection.filter(c => c !== category);
        // If nothing selected, revert to All
        if (newSelection.length === 0) {
          onSelectionChange("All");
        } else {
          onSelectionChange(newSelection);
        }
      } else {
        // Select this type (add to selection)
        onSelectionChange([...currentSelection, category]);
      }
    }
  };
  
  const isTypeSelected = (category: DealCategory): boolean => {
    if (isAllSelected) return false;
    return (selectedTypes as DealCategory[]).includes(category);
  };
  
  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      <span className="text-sm font-medium text-muted-foreground mr-1">Type:</span>
      
      {/* All Button */}
      <button
        onClick={handleAllClick}
        className={cn(
          "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border",
          isAllSelected
            ? "bg-primary text-primary-foreground border-primary"
            : "bg-background text-muted-foreground border-border hover:bg-muted hover:text-foreground"
        )}
      >
        {isAllSelected && <Check className="w-3.5 h-3.5" />}
        All
      </button>
      
      {/* Category Buttons */}
      {DEAL_CATEGORIES.map((category) => {
        const Icon = CATEGORY_ICONS[category];
        const label = getDealCategoryLabel(category);
        const colors = getDealCategoryColor(category);
        const selected = isTypeSelected(category);
        
        // Hide Unclassified when disabled
        if (category === "UNCLASSIFIED" && disableUnclassified) {
          return null;
        }
        
        return (
          <button
            key={category}
            onClick={() => handleTypeClick(category)}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border",
              selected
                ? cn(colors.bg, colors.text, colors.border)
                : "bg-background text-muted-foreground border-border hover:bg-muted hover:text-foreground"
            )}
          >
            {selected && <Check className="w-3.5 h-3.5" />}
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        );
      })}
    </div>
  );
}